import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@agion/ui'
import { Activity, CheckCircle, AlertTriangle, Clock } from 'lucide-react'
import type { PlatformStats } from '@/core/observability_types'
import { StatCard } from './StatCard'

interface StatsRowProps {
  stats: PlatformStats | null
  loading: boolean
}

export const StatsRow: React.FC<StatsRowProps> = ({ stats, loading }) => {
  const { t } = useTranslation('pages')

  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="py-4">
              <div className="h-16 animate-pulse bg-slate-100 dark:bg-slate-700 rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const errorPct = (stats.globalErrorRate * 100).toFixed(2)
  const errorColor = stats.globalErrorRate > 0.05 ? 'bg-red-500' : stats.globalErrorRate > 0.01 ? 'bg-amber-500' : 'bg-emerald-500'

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        icon={Activity}
        label={t('observability.totalServices', 'Services')}
        value={`${stats.healthyServices}/${stats.totalServices}`}
        subtext={t('observability.healthy', 'healthy')}
        color="bg-blue-500"
      />
      <StatCard
        icon={CheckCircle}
        label={t('observability.requestRate', 'Request Rate')}
        value={`${stats.totalRequestRate.toFixed(1)}/s`}
        color="bg-emerald-500"
      />
      <StatCard
        icon={AlertTriangle}
        label={t('observability.errorRate', 'Error Rate')}
        value={`${errorPct}%`}
        color={errorColor}
      />
      <StatCard
        icon={Clock}
        label={t('observability.p95Latency', 'P95 Latency')}
        value={stats.globalP95Latency < 1000 ? `${stats.globalP95Latency.toFixed(0)}ms` : `${(stats.globalP95Latency / 1000).toFixed(2)}s`}
        color="bg-violet-500"
      />
    </div>
  )
}
